/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  PropType,
  nextTick,
} from "vue";

interface Option {
  [key: string]: any;
}

export default defineComponent({
  name: "c-multi-check",

  props: {
    value: {
      validator: () => true,
    },
    loading: { type: Boolean, default: false },
    label: { type: String, default: "" },
    error: { type: [Boolean, String], default: false },
    showError: { type: [Boolean, String], default: true },
    style: { type: String, default: "" },
    class: { type: String, default: "" },
    optionValue: { type: String as PropType<string>, default: "value" },
    optionLabel: { type: String as PropType<string>, default: "label" },
    schema: { type: Function as PropType<() => void>, default: () => ({}) },
    options: { type: Array as PropType<Option[]>, default: () => [] },
    searchable: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    placeholder: { type: String, default: "" },
    clear: { type: Boolean, default: true },
    required: { type: Boolean, default: false },
    filter: { type: String, default: "status" },
    filterValue: { type: [Boolean, Number, String], default: 1 },
    isTop: { type: Boolean, default: false },
  },

  emits: ["update:value", "enter", "onOptions"],

  setup(props, { emit }) {
    const mockValue = computed(() => props.value);
    const hasKey = ref(false);
    const className = ref<string>(props.class);
    const styleName = ref<string>(props.style);
    const isRequired = ref<boolean>(false);
    const updatedLabel = ref<string>("");
    const isDropdown = ref<boolean>(false);
    const refIds = "cSelectDropdown";

    const endpoint = "";
    const keySearch = ref("");
    const keyType = "";
    const optionList = ref<Option[]>(getOptions());
    const tempOptions = ref<Option[]>(getOptionsTemp());
    const selectedValue = computed(() => {
      return Array.isArray(mockValue.value) &&
        mockValue.value &&
        mockValue.value.length
        ? mockValue.value
        : [];
    });
    const isCreateSelect = ref<boolean>(false);

    const isError = computed(() => {
      return props.error && String(props.error);
    });

    const listRequired = () => {
      if (props.label && props.label.includes("*")) {
        updatedLabel.value = props.label.replace(/\*/g, "");
        isRequired.value = true;
      } else {
        isRequired.value = false;
        updatedLabel.value = props.label;
      }
    };

    function getOptions() {
      return flatValues();
    }

    function getOptionsTemp() {
      return flatValues();
    }

    function flatValues() {
      let newOptions: Option[] = props.options;
      let option: Option | undefined = undefined;

      if (props.options.length > 0) {
        hasKey.value = Object.prototype.hasOwnProperty.call(
          props.options[0],
          props.filter
        );
      }

      if (Array.isArray(mockValue.value)) {
        const arrOption = mockValue.value.flatMap((value) =>
          props.options.filter(
            (opt) => opt[props.optionValue] === value[props.optionValue]
          )
        );

        if (hasKey.value) {
          newOptions = newOptions.filter(
            (opt) => opt[props.filter] === props.filterValue
          );
          newOptions.push(
            ...arrOption.filter((opt) => !newOptions.includes(opt))
          );
        }
      } else {
        option = props.options.find(
          (item) => item[props.optionValue] === mockValue.value
        );

        if (hasKey.value) {
          newOptions = newOptions.filter(
            (opt) => opt[props.filter] === props.filterValue
          );
          if (option && !newOptions.includes(option)) {
            newOptions.push(option);
          }
        }
      }

      return newOptions;
    }

    const onFilter = () => {
      if (endpoint && props.searchable) {
        if (keySearch.value && keySearch.value.length > 3) {
          debounce(queriesShorten, 200000);
        } else {
          if (keySearch.value.length === 0) {
            debounce(queriesShorten, 200000);
          }
        }

        // Emit event
        emit(
          "onOptions",
          optionList.value && optionList.value.length
            ? optionList.value
            : tempOptions.value
        );
      }

      if (!props.options) {
        return;
      }

      optionList.value = flatValues().filter((item) => {
        let option = item;

        if (props.optionLabel) {
          option = item[props.optionLabel];
        }

        return option
          .toString()
          .toLowerCase()
          .includes(keySearch.value.toLowerCase());
      });
    };

    const findActiveOption = (option: string) => {
      return selectedValue.value.some((value: Option) => {
        const optionLabel = props.optionLabel;
        return value[optionLabel] === option;
      });
    };

    const queriesShorten = () => {
      const fullUrl =
        endpoint + (keySearch.value ? `&${[keyType]}=${keySearch.value}` : ``);

      // Call your function for dynamic endpoint
      console.log(fullUrl);
    };

    const debounce = (fn: (...args: any[]) => void, delay: number) => {
      let timeoutId: ReturnType<typeof setTimeout>;

      return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fn(...args);
        }, delay);
      };
    };

    onMounted(() => {
      listRequired();
    });

    const onSelectValue = (value: any): void => {
      const existingIndex = selectedValue.value.findIndex(
        (v) => v[props.optionValue] === value[props.optionValue]
      );

      if (existingIndex !== -1) {
        selectedValue.value.splice(existingIndex, 1);
      } else {
        selectedValue.value.push(value);
      }

      emit("update:value", selectedValue.value);
      props.schema();

      if (!isCreateSelect.value) {
        isDropdown.value = false;
      }

      if (props.searchable) {
        nextTick(() => {
          if (isDropdown.value) {
            (keySearchQuery.value as HTMLInputElement).focus();
          }
        });
      }
    };

    const deleteOptionValue = (value: string) => {
      if (props.readonly) {
        return;
      }

      const index = selectedValue.value.indexOf(value);

      if (index !== -1) {
        selectedValue.value.splice(index, 1);
      }

      props.schema();
    };

    const outsideClick = (): void => {
      isDropdown.value = false;
    };

    const isLoading = computed(() => {
      return !!props.loading;
    });

    const keySearchQuery = ref<HTMLElement | null>(null);

    const onSelect = (): void => {
      if (props.readonly) {
        return;
      }

      isDropdown.value = !isDropdown.value;
      keySearch.value = "";
      onFilter();

      if (props.searchable) {
        nextTick(() => {
          if (isDropdown.value) {
            (keySearchQuery.value as HTMLInputElement).focus();
          }
        });
      }
    };

    const focusIn = (): void => {
      isDropdown.value = true;
    };

    const focusOut = (): void => {
      isDropdown.value = false;
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        isDropdown.value = false;
      }
    };

    const toggleCreateSelect = (): void => {
      isCreateSelect.value = !isCreateSelect.value;

      if (props.searchable) {
        nextTick(() => {
          if (isDropdown.value) {
            (keySearchQuery.value as HTMLInputElement).focus();
          }
        });
      }
    };

    const showPopup = ref(false);

    const copyToClipboard = () => {
      const textArea = document.createElement("textarea");
      let valueToCopy = props.value;

      if (Array.isArray(props.value) || typeof props.value === "object") {
        valueToCopy = JSON.stringify(props.value);
      }

      textArea.value = String(valueToCopy);
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      showPopup.value = true;

      setTimeout(() => {
        showPopup.value = false;
      }, 1500);
    };

    // start manual input
    const deleteValue = (): void => {
      emit("update:value", []);
      isDropdown.value = false;
      props.schema();
    };

    return {
      refIds,
      className,
      styleName,
      isLoading,
      isError,
      isRequired,
      updatedLabel,
      onSelect,
      isDropdown,
      onSelectValue,
      outsideClick,
      focusIn,
      focusOut,
      handleKeyDown,
      selectedValue,
      optionList,
      onFilter,
      keySearch,
      findActiveOption,
      deleteOptionValue,
      keySearchQuery,
      isCreateSelect,
      toggleCreateSelect,
      copyToClipboard,
      showPopup,

      deleteValue,
    };
  },
});
